/// <reference lib="dom"/>
import { CalcEnergies, CalcEnergyBins, FrequencyRanges } from "./energyCalc.tsx";
import { AmpToDb } from "./spectrum.tsx";

//#region level
export interface LiveLevel {
  rms: number;
  rmsDb: number;
  peak: number;
  peakDb: number;
  crestDb: number;
  clipped: number;
}

export function LevelOf(timeDomain: Float32Array, clipThreshold = 0.999): LiveLevel {
  let sum = 0;
  let peak = 0;
  let clipped = 0;
  for (let i = 0; i < timeDomain.length; i++) {
    const a = Math.abs(timeDomain[i]);
    sum += a * a;
    if (a > peak) peak = a;
    if (a >= clipThreshold) clipped++;
  }
  const rms = Math.sqrt(sum / Math.max(1, timeDomain.length));
  const rmsDb = AmpToDb(rms);
  const peakDb = AmpToDb(peak);
  return { rms, rmsDb, peak, peakDb, crestDb: peakDb - rmsDb, clipped };
}
//#endregion

//#region gate
export interface LiveGateParams {
  openDb: number;
  closeDb: number;
  hold: number;
}

export function CreateGate() {
  let open = false;
  let lastAbove = -Infinity;
  return (rmsDb: number, time: number, params: LiveGateParams): boolean => {
    if (rmsDb >= params.closeDb) lastAbove = time;
    if (!open && rmsDb >= params.openDb) open = true;
    else if (open && rmsDb < params.closeDb && time - lastAbove >= params.hold) open = false;
    return open;
  };
}
//#endregion

//#region pitch
export function Cents(freq: number, target: number): number {
  return freq > 0 && target > 0 ? 1200 * Math.log2(freq / target) : 0;
}

export function HzToMidi(hz: number): number {
  return hz > 0 ? 69 + 12 * Math.log2(hz / 440) : 0;
}
//#endregion

//#region energy
export type FrequencyBand = keyof typeof FrequencyRanges;

export const FrequencyBands = Object.keys(FrequencyRanges) as FrequencyBand[];

export interface LiveEnergyParams {
  bins: number;
  log: boolean;
  minHz: number;
  maxHz: number;
}

export interface LiveEnergy {
  total: number;
  bands: Record<FrequencyBand, number>;
  bins: number[];
}

export function EnergyOf(
  byteFreq: Uint8Array,
  sampleRate: number,
  params: LiveEnergyParams,
): LiveEnergy {
  const bands = Object.fromEntries(
    FrequencyBands.map((b) => {
      const { low, high } = FrequencyRanges[b];
      return [b, CalcEnergies(low, high, byteFreq, sampleRate) / 255];
    }),
  ) as Record<FrequencyBand, number>;
  const bins = params.bins > 0
    ? CalcEnergyBins(params.log, params.bins, params.minHz, params.maxHz, byteFreq, sampleRate)
      .map((v) => v / 255)
    : [];
  return {
    total: CalcEnergies(params.minHz, params.maxHz, byteFreq, sampleRate) / 255,
    bands,
    bins,
  };
}
//#endregion

//#region spectral
export interface LiveSpectral {
  centroid: number;
  rolloff: number;
  flatness: number;
  flux: number;
}

export function DbToMagnitude(freqDb: Float32Array, out = new Float32Array(freqDb.length)) {
  for (let i = 0; i < freqDb.length; i++) {
    const db = freqDb[i];
    out[i] = Number.isFinite(db) ? Math.pow(10, db / 20) : 0;
  }
  return out;
}

export function CreateSpectral() {
  let prev: Float32Array | null = null;
  return (freqDb: Float32Array, sampleRate: number, rolloffRatio = 0.85): LiveSpectral => {
    const mag = DbToMagnitude(freqDb);
    const n = mag.length;
    const binHz = sampleRate / 2 / Math.max(1, n);
    const eps = 1e-12;

    let sum = 0;
    let weighted = 0;
    let logSum = 0;
    for (let i = 0; i < n; i++) {
      sum += mag[i];
      weighted += mag[i] * i * binHz;
      logSum += Math.log(mag[i] + eps);
    }

    let rolloff = 0;
    const limit = sum * rolloffRatio;
    for (let i = 0, acc = 0; i < n; i++) {
      acc += mag[i];
      if (acc >= limit) {
        rolloff = i * binHz;
        break;
      }
    }

    let flux = 0;
    if (prev && prev.length === n) {
      for (let i = 0; i < n; i++) {
        const d = mag[i] - prev[i];
        if (d > 0) flux += d;
      }
      flux /= Math.max(1, n);
    }
    prev = mag;

    return {
      centroid: sum > eps ? weighted / sum : 0,
      rolloff,
      flatness: sum > eps ? Math.exp(logSum / Math.max(1, n)) / (sum / Math.max(1, n)) : 0,
      flux,
    };
  };
}
//#endregion

//#region onset
export interface LiveOnsetParams {
  threshold: number;
  window: number;
  minInterval: number;
}

export function CreateFluxOnsetDetector() {
  const history: number[] = [];
  let prevFlux = 0;
  let last = -Infinity;
  return (flux: number, time: number, armed: boolean, params: LiveOnsetParams): number | null => {
    let strength: number | null = null;
    if (history.length >= Math.min(4, params.window)) {
      const mean = history.reduce((a, b) => a + b, 0) / history.length;
      const variance = history.reduce((a, b) => a + (b - mean) * (b - mean), 0) /
        history.length;
      const std = Math.sqrt(variance);
      const score = (flux - mean) / (std + 1e-9);
      if (
        armed && flux > prevFlux && score >= params.threshold &&
        time - last >= params.minInterval
      ) {
        strength = score;
        last = time;
      }
    }
    history.push(flux);
    while (history.length > params.window) history.shift();
    prevFlux = flux;
    return strength;
  };
}
//#endregion
